/**
 * Schemas for the Reservation Panel feature (reservation-panel OpenSpec change).
 *
 * Separate from the existing AuditLogSchema — AuditEventSchema is a distinct shape
 * for the per-media-unit audit timeline endpoint, including nullable source and actorUserName.
 *
 * Pagination: offset-based (limit/offset/total), NOT cursor-based.
 *
 * REQ-4.6, REQ-4.5, REQ-3.1 → reservation-panel, action-permissions
 */
import { z } from 'zod';
import { ReservationStatusSchema, ReservationSourceSchema } from './reservation-status.js';

// ─── Queue Position ─────────────────────────────────────────────────────────

export const QueuePositionSchema = z
  .object({
    index: z.number().int().nonnegative(),
    total: z.number().int().positive(),
  })
  .nullable();

export type QueuePosition = z.infer<typeof QueuePositionSchema>;

// ─── ReservationListItemSchema ───────────────────────────────────────────────

/**
 * A reservation row as returned by GET /reservations and GET /media-units/:id/reservations.
 * Includes all ReservationRecord fields plus denormalized labels and server-computed queue position.
 *
 * Note: Does NOT extend ReservationRecordSchema from reservations.endpoints.ts to avoid a circular
 * import (reservations.endpoints.ts imports this file for the endpoint definitions).
 * The field list is kept in sync manually with ReservationRecordSchema.
 *
 * - mediaUnitName: maps to media_units.grw_id (no standalone 'name' column)
 * - mediaUnitSupplierNumber: maps to media_units.supplier_number (nullable)
 * - holderUserName: NOT NULL (reservations.holder_user_id is NOT NULL)
 * - offerName: nullable (reservations.offer_id is nullable)
 * - clientName: nullable — offers.client_id -> clients.name (reservation may have no offer)
 * - salespersonName: nullable — offers.salesperson_id -> users.name (offer's salesperson is optional)
 * - queuePosition: nullable (null when no queue position can be computed, e.g. SOLD/OPTION)
 * - effectiveExpiresAt: server-authoritative effective release boundary — ttlExpiresAt for
 *   SOFT_RESERVATION, the later non-null instant of ttlExpiresAt/signingDeadlineAt for
 *   HARD_RESERVATION, null for OPTION/SOLD. Exposed so web eligibility/deadline-display logic
 *   never needs to recompute this from a missing signingDeadlineAt field (this schema
 *   deliberately does not expose signingDeadlineAt itself — see manual-reservation-expiry-editing
 *   / reservation-engine "D-1"). REQ-4.4, REQ-4.6.
 */
export const ReservationListItemSchema = z.object({
  // ReservationRecord base fields (kept in sync with ReservationRecordSchema in reservations.endpoints.ts)
  id: z.number().int().positive(),
  mediaUnitId: z.number().int().positive(),
  offerId: z.number().int().positive().nullable(),
  holderUserId: z.number().int().positive(),
  status: ReservationStatusSchema,
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  targetVisibilityDays: z.number().int().positive().nullable(),
  ttlExpiresAt: z.coerce.date().nullable(),
  effectiveExpiresAt: z.coerce.date().nullable(),
  source: ReservationSourceSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // Denormalized labels
  mediaUnitName: z.string(),
  mediaUnitSupplierNumber: z.string().nullable(),
  holderUserName: z.string(),
  offerName: z.string().nullable(),
  /** Client behind this reservation's offer, when it has one (CU-86cb7ctfw). */
  clientName: z.string().nullable(),
  /** Salesperson ("handlowiec") owning this reservation's offer, when set (CU-86cb7ctfw). */
  salespersonName: z.string().nullable(),
  queuePosition: QueuePositionSchema,
  /**
   * Number of `reservation_queue` entries whose date window overlaps this reservation's window.
   * Used for the repurposed read-only "Kolejka" waiting-count column on the global list.
   * A single waiting entry overlapping N reservations is counted under each of them.
   * REQ-4.2, REQ-4.6 → reservation-waitlist / List queue position and per-reservation waiting count
   */
  waitingCount: z.number().int().nonnegative(),
  /**
   * Set when the reservation was released via cancellation/archival instead of the
   * (removed) hard delete. Null for active reservations. Only rows with a non-null
   * archivedAt are surfaced when the caller passes includeArchived=true.
   * REQ-4.1 → reservation-engine / Reservation archive replaces row deletion
   */
  archivedAt: z.coerce.date().nullable(),
});

export type ReservationListItem = z.infer<typeof ReservationListItemSchema>;

// ─── AuditEventSchema ────────────────────────────────────────────────────────

/**
 * Audit event row for the per-media-unit audit timeline.
 * Distinct from AuditLogSchema (which omits source and has different field set).
 *
 * - actorUserName: nullable for system actors (TTL worker, auto-extension)
 * - source: nullable in the DB (audit_logs.source has no NOT NULL constraint)
 */
export const AuditEventSchema = z.object({
  id: z.number(),
  action: z.string(),
  actorUserName: z.string().nullable(),
  createdAt: z.coerce.date(),
  source: z.string().nullable(),
  entityId: z.number().nullable(),
  details: z.unknown().nullable(),
});

export type AuditEvent = z.infer<typeof AuditEventSchema>;

// ─── Sort keys ───────────────────────────────────────────────────────────────

/**
 * Canonical sort key list for reservation list endpoints.
 * Single source of truth — used by ReservationListQuerySchema, UnitReservationQuerySchema,
 * and (mirrored) by packages/db SORT_COLUMNS map.
 */
export const RESERVATION_SORT_KEYS = ['date_from', 'date_to', 'created_at', 'status'] as const;

export type ReservationSortKey = (typeof RESERVATION_SORT_KEYS)[number];

// ─── ReservationListQuerySchema ──────────────────────────────────────────────

/**
 * Coerces the includeArchived query param correctly:
 * - "true" -> true, "false" -> false, "1" -> true, "0" -> false
 * - z.coerce.boolean() converts "false" to true (non-empty string), so we use a transform instead.
 */
const queryBoolean = z
  .string()
  .transform((v) => v === 'true' || v === '1')
  .or(z.boolean())
  .optional();

/**
 * Query parameters for GET /reservations.
 * Offset-based pagination (NOT cursor-based — existing createPaginatedRequestSchema uses cursor).
 */
export const ReservationListQuerySchema = z.object({
  status: z.array(ReservationStatusSchema).optional(),
  holderUserId: z.array(z.coerce.number().int().positive()).optional(),
  offerId: z.array(z.coerce.number().int().positive()).optional(),
  mediaUnitId: z.array(z.coerce.number().int().positive()).optional(),
  source: z.array(ReservationSourceSchema).optional(),
  /** Client behind the reservation's offer (CU-86cb7ctfw). */
  clientId: z.array(z.coerce.number().int().positive()).optional(),
  /** Salesperson owning the reservation's offer (CU-86cb7ctfw). */
  salespersonId: z.array(z.coerce.number().int().positive()).optional(),
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  sort: z.enum(RESERVATION_SORT_KEYS).default('date_from'),
  order: z.enum(['asc', 'desc']).default('asc'),
  /**
   * When true, includes archived (cancelled/released) reservations in the result.
   * Defaults to false — active lists skip the archive unless explicitly requested.
   * REQ-4.1 → reservation-engine / Reservation archive replaces row deletion
   */
  includeArchived: queryBoolean,
  /**
   * When true, restricts the list to reservations with at least one overlapping
   * reservation_queue entry — the "Tylko z kolejką" scope pill (CU-86cb7ctfw).
   */
  hasQueue: queryBoolean,
});

export type ReservationListQuery = z.infer<typeof ReservationListQuerySchema>;

// ─── ReservationListResponseSchema ──────────────────────────────────────────

export const ReservationListResponseSchema = z.object({
  rows: z.array(ReservationListItemSchema),
  total: z.number().int().nonnegative(),
});

export type ReservationListResponse = z.infer<typeof ReservationListResponseSchema>;

// ─── UpdateReservationDatesSchema ────────────────────────────────────────────

export const UpdateReservationDatesSchema = z.object({
  id: z.coerce.number().int().positive(),
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  expectedStatus: ReservationStatusSchema,
  force: z.boolean().optional(),
});

export type UpdateReservationDates = z.infer<typeof UpdateReservationDatesSchema>;

// ─── OverrideReservationSchema ───────────────────────────────────────────────

export const OverrideReservationSchema = z.object({
  id: z.coerce.number().int().positive(),
  targetStatus: ReservationStatusSchema,
  reason: z.string().min(1),
  /**
   * Optional manual TTL expiry override for SOFT_RESERVATION/HARD_RESERVATION
   * targets (temp holds). Ignored server-side for SOLD/OPTION targets, which
   * always clear ttlExpiresAt per the existing invariant (no TTL sweep for them).
   * Uses `.nullish()` (not `.optional()`) because `.optional()` only special-cases
   * `undefined` — an explicit JSON `null` would otherwise be coerced by
   * `z.coerce.date()` via `new Date(null)`, which evaluates to the valid but
   * wrong epoch date `1970-01-01T00:00:00.000Z` instead of "no override".
   */
  ttlExpiresAt: z.coerce.date().nullish(),
});

export type OverrideReservation = z.infer<typeof OverrideReservationSchema>;

// ─── Audit Event List Query + Response ──────────────────────────────────────

/**
 * Query params for GET /media-units/:id/audit-events.
 * Higher limit cap (200) than the main reservation list (100).
 */
export const AuditEventListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export type AuditEventListQuery = z.infer<typeof AuditEventListQuerySchema>;

export const AuditEventListResponseSchema = z.object({
  events: z.array(AuditEventSchema),
  total: z.number().int().nonnegative(),
});

export type AuditEventListResponse = z.infer<typeof AuditEventListResponseSchema>;

// ─── Unit Reservation Query (for GET /media-units/:id/reservations) ──────────

/**
 * Query params for GET /media-units/:id/reservations.
 * Same filters as ReservationListQuerySchema but with higher limit cap (200)
 * and default sort of date_from DESC per spec.
 */
export const UnitReservationQuerySchema = z.object({
  status: z.array(ReservationStatusSchema).optional(),
  holderUserId: z.array(z.coerce.number().int().positive()).optional(),
  offerId: z.array(z.coerce.number().int().positive()).optional(),
  source: z.array(ReservationSourceSchema).optional(),
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).default(0),
  sort: z.enum(RESERVATION_SORT_KEYS).default('date_from'),
  order: z.enum(['asc', 'desc']).default('desc'),
});

export type UnitReservationQuery = z.infer<typeof UnitReservationQuerySchema>;
